import type { ProfileGateway } from './ProfileGateway';
import type { ProfilePhotoResponse, ProfileResponse, UpdateProfileRequest, UpdateProfileResponse } from './types';
import { refreshAccessToken } from '../../shared/tokenRefresh';
import { apiErrorFromResponse } from '../../shared/apiErrorFromResponse';

/** Talks to the real API. See HttpIdentityGateway's own doc comment for why every call uses
 * `credentials: 'include'` and a relative path rather than an absolute cross-origin URL. */
export class HttpProfileGateway implements ProfileGateway {
  // `signal` is a separate parameter, not folded into `init`, because RequestInit.signal is typed as
  // `AbortSignal | null` and this project's exactOptionalPropertyTypes rejects assigning it a plain
  // `undefined` — building the init object conditionally here keeps every call site simple.
  private async request<T>(path: string, init?: RequestInit, signal?: AbortSignal): Promise<T> {
    const doFetch = () =>
      fetch(path, {
        ...init,
        ...(signal ? { signal } : {}),
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...init?.headers },
      });

    let response = await doFetch();

    // The access-token cookie is short-lived (15 min) — a 401 here is very often just that it expired
    // mid-session, not a real auth failure. See shared/tokenRefresh.ts.
    if (response.status === 401 && (await refreshAccessToken())) {
      response = await doFetch();
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(response, `Request to ${path} failed with ${response.status}`);
    }

    // Results.Ok() with no value (confirm-email-change) sends status 200 with a genuinely empty body, not
    // 204 — response.json() throws "Unexpected end of JSON input" on that empty string, so an empty body
    // is treated as "no value" regardless of which 2xx status carried it. See HttpIdentityGateway's own
    // identical fix for the same bug, first reproduced live against this exact endpoint.
    const text = await response.text();
    return (text ? JSON.parse(text) : undefined) as T;
  }

  getProfile(signal?: AbortSignal): Promise<ProfileResponse> {
    return this.request('/api/me/profile', undefined, signal);
  }

  updateProfile(request: UpdateProfileRequest): Promise<UpdateProfileResponse> {
    return this.request('/api/me/profile', { method: 'PUT', body: JSON.stringify(request) });
  }

  async confirmEmailChange(token: string): Promise<void> {
    await this.request('/api/me/profile/confirm-email-change', { method: 'POST', body: JSON.stringify({ token }) });
  }

  async uploadPhoto(file: File): Promise<ProfilePhotoResponse> {
    const formData = new FormData();
    formData.append('file', file);

    // No JSON Content-Type here — the browser sets multipart/form-data with the right boundary itself.
    const doUpload = () => fetch('/api/me/profile/photo', { method: 'POST', credentials: 'include', body: formData });
    let response = await doUpload();

    if (response.status === 401 && (await refreshAccessToken())) {
      response = await doUpload();
    }

    if (!response.ok) {
      throw await apiErrorFromResponse(response, `Photo upload failed with ${response.status}`);
    }

    return (await response.json()) as ProfilePhotoResponse;
  }

  getPhotoUrl(hasProfilePhoto: boolean): string | null {
    // A same-origin <img src> sends the auth cookie automatically — no separate fetch/blob step needed.
    // The query param exists only to bust the browser's image cache after a new photo is uploaded.
    return hasProfilePhoto ? `/api/me/profile/photo?v=${Date.now()}` : null;
  }
}
